// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * TypeScript types for Email Notification Admin functionality.
 * Matches backend DTOs from resource-server.
 */

export enum NotificationStatus {
  PENDING = 'PENDING',
  SENT = 'SENT',
  FAILED = 'FAILED',
  RETRY_SCHEDULED = 'RETRY_SCHEDULED'
}

export enum NotificationType {
  ADVISOR_PROGRESS = 'ADVISOR_PROGRESS',
  ADVISOR_COMPLETION = 'ADVISOR_COMPLETION',
  PROVIDER_NOTIFICATION = 'PROVIDER_NOTIFICATION'
}

export enum RecipientType {
  ADVISOR = 'ADVISOR',
  PROVIDER = 'PROVIDER'
}

/**
 * Email notification details.
 */
export interface EmailNotification {
  id: string;
  loaId: string;
  notificationType: NotificationType;
  recipientType: RecipientType;
  recipientEmail: string;
  signatoryId?: string;
  signatoryName?: string;
  signedCount?: number;
  totalSignatories?: number;
  emailTemplateId?: string;
  templateLevel?: string;
  subject: string;
  hasAttachment: boolean;
  attachmentFilename?: string;
  status: NotificationStatus;
  sentAt?: string;
  deliveredAt?: string;
  retryCount: number;
  maxRetries: number;
  nextRetryAt?: string;
  errorMessage?: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * Overall email notification statistics.
 */
export interface EmailNotificationStats {
  totalSent: number;
  totalFailed: number;
  totalPending: number;
  successRate: number;
  avgRetryCount: number;
}

/**
 * Daily statistics for charting.
 */
export interface DailyStats {
  date: string; // LocalDate format: yyyy-MM-dd
  sent: number;
  failed: number;
  pending: number;
  total: number;
}

/**
 * Brokerage-specific statistics.
 */
export interface BrokerageStats {
  brokerageId: string;
  brokerageName: string;
  sent: number;
  failed: number;
  total: number;
  successRate: number;
}

/**
 * Pagination response wrapper.
 */
export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

/**
 * Filter parameters for listing notifications.
 */
export interface NotificationFilters {
  brokerageId?: string;
  status?: NotificationStatus;
  notificationType?: NotificationType;
  startDate?: string; // ISO format: yyyy-MM-dd'T'HH:mm:ss
  endDate?: string; // ISO format: yyyy-MM-dd'T'HH:mm:ss
}

/**
 * Pagination parameters.
 */
export interface PaginationParams {
  page?: number;
  size?: number;
}
